import { MoveRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "../Reveal";
import { SITE } from "../../config/site";

export const CalculatorPreview = ({ calculator, slug }) => {
  const appUrl = `${SITE.appBaseUrl}${calculator.path}`;

  return (
    <section className="bg-ink-bg py-24 sm:py-32" data-testid="calculator-preview-section">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-accent">The Instrument</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl uppercase leading-none tracking-tight text-ink-text sm:text-4xl lg:text-5xl">
            The {calculator.name}, up close
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            className="mt-12 border border-[#2A2A2A] bg-[#141414] shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
            data-testid="calculator-preview-card"
          >
            <div className="flex items-center justify-between border-b border-[#2A2A2A] px-6 py-4 sm:px-8">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/50">
                MC360 // {slug}
              </span>
              <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-accent">
                <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
                Live in app
              </span>
            </div>

            <div className="grid gap-10 p-6 sm:p-10 md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-8">
              <div data-testid="calculator-inputs">
                <p className="mb-5 font-mono text-xs uppercase tracking-[0.25em] text-white/50">Inputs</p>
                <div className="space-y-3">
                  {calculator.inputs.map((input, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between border border-[#2A2A2A] px-4 py-3"
                      data-testid={`calculator-input-${i}`}
                    >
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/60">
                        {input.label}
                      </span>
                      <span className="font-mono text-lg font-bold text-white">{input.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="hidden justify-center md:flex" aria-hidden="true">
                <span className="flex h-12 w-12 items-center justify-center border border-accent">
                  <MoveRight className="h-5 w-5 text-accent" />
                </span>
              </div>

              <div data-testid="calculator-outputs">
                <p className="mb-5 font-mono text-xs uppercase tracking-[0.25em] text-white/50">Outputs</p>
                <div className="space-y-4">
                  {calculator.outputs.map((output, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-4" data-testid={`calculator-output-${i}`}>
                      <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/60">
                        {output.label}
                      </span>
                      <span
                        className={
                          output.highlight
                            ? "font-mono text-3xl font-bold tracking-tight text-accent [text-shadow:0_0_24px_rgba(244,123,32,0.5)]"
                            : "font-mono text-xl font-bold tracking-tight text-white"
                        }
                      >
                        {output.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start justify-between gap-4 border-t border-[#2A2A2A] px-6 py-6 sm:flex-row sm:items-center sm:px-8">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">
                Opens the live calculator on matcalc360.pro
              </p>
              <a
                href={appUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calculator-cta-button"
                className="inline-flex items-center gap-2 bg-accent px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-accent-dark hover:shadow-[0_0_32px_rgba(244,123,32,0.45)]"
              >
                Run your own numbers
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
